import { CaretLeftOutlined, EditOutlined, InfoCircleTwoTone } from "@ant-design/icons";
import type { FormListFieldData } from "antd";
import { Button, Form, Input, message, Modal, Splitter } from "antd";
import SchemaPromotionAction from "components/molecules/SchemaPromotionAction";
import { Rule } from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import fieldDataTypes from "common/data/fieldDataTypes";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import Tooltip from "components/atoms/Tooltip";
import SchemaModelFieldListReadOnly from "components/molecules/SchemaModelFieldListReadOnly";
import SchemaModelFieldList from "components/organisms/SchemaModelFieldList";
import SchemaModelFieldProperties from "components/organisms/SchemaModelFieldProperties/index";
import dayjs from "dayjs";
import _ from "lodash";
import React, { useCallback, useEffect } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

interface IManageSchemaModelFormProps {
    schemaModel: ISchemaModel;
    nameRules?: Rule[];
    descriptionRules?: Rule[];
    readOnly?: boolean;
    allowSave?: boolean;
    header?: React.ReactNode; // To add more buttons to header
    footer?: React.ReactNode; // To add more buttons to footer
    userCommentsPermission?: IPermissionState;
    openFieldCommentId?: string;
    openFieldAnnotationId?: string;
    businessAreaDataList?: IBusinessAreaData[];
    getBusinessAreaDataForBusinessAreaResult?: IResultState;
    getDataTagsByBusinessAreaIdResult?: IResultState;
    dataTagsByBusinessAreaIdResult?: { Items?: any } & IResultState;
    autoTaggingResult?: { error?: string; loading?: boolean } & IResultState;
    autoTaggingData?: any;
    formErrors?: any;
    onFinish: (schemaModel: ISchemaModel, afterSaveCallback?: () => void) => void;
    getFormErrors: (name: string) => Object;
    onSwitchEditMode?: (id: string) => void;
    onCancel: () => void;
    onExportSchema: (schemaId: string) => void;
    getFieldRowLevelError: (index: number) => string | number | null;
    autoTagButton?: {
        onClick: () => void;
        disabled?: boolean;
        disabledTooltipTitle?: string;
        onAutoTagging: (payload: any) => void;
        onAutoTagCancel?: () => void;
    };
    isAutoTagging?: boolean;
    isFetchingTags?: boolean;
    isAutoTagRequested?: boolean;
    autoTagError?: boolean;
}
export interface IManageSchemaModelFormRef {
    onApplyAutoTagSuggestions: (selectedSuggestions: any) => void;
    requestAutoTagging: () => void;
    performAutoTagging: (classifierTags?: any[]) => void;
    showNoTagsWarning: () => void;
    applyAiPiiSuggestions: (updatedFields: any[]) => void;
    applyAiFieldDescriptions: (updatedFields: any[], schemaDescription?: string | null) => void;
}

type FieldDeleteContext = {
    fieldIndex: number;
    fieldData?: ISchemaField | null;
};

const FIELD_PROP_LABELS: Record<string, string> = {
    name: "Name",
    description: "Description",
    dataType: "Data Type",
    length: "Length",
    min: "Min",
    max: "Max",
    range: "Range",
    precision: "Precision",
    scale: "Scale",
    domainDatasetId: "Linked Domain Dataset",
    picklistType: "Picklist Type",
    staticPicklist: "Picklist Values",
    defaultValue: "Default Value",
    dateFormat: "Date Format",
    dateSeparator: "Date Separator",
    timeFormat: "Time Format",
    survivorshipRule: "Survivorship Rule",
    regex: "Validation Pattern",
};

const humanizeFieldProp = (prop: string): string => FIELD_PROP_LABELS[prop] || prop;

/**
 * Field properties that the backend (schema-service `createFieldSchema.js` / `updateFieldSchema.js`)
 * marks conditionally required per dataType, but which the UI only ever seeds via an antd
 * `Form.Item initialValue`. Those props are invisible to the form's `onValuesChange` handler, and the
 * Form.Item only registers its initialValue once MOUNTED - i.e. only if the user actually clicks into
 * that field's properties panel. Either way the value shows in the UI but never reaches the payload,
 * and the save fails with "<prop> is required" on a field that looks correctly configured.
 *
 * Keep these in sync with the panels' `initialValue` props:
 *   BooleanFieldProperties, LatLongFieldProperties, LontiudeandLatitudeFieldproperties,
 *   AttachmentFieldProperty, PicklistFieldProperties.
 */
const DATA_TYPE_REQUIRED_DEFAULTS: Record<string, Record<string, any>> = {
    boolean: { allowedBooleanValues: "y/n" },
    latlng: { precision: 4, latitudeRange: { min: -90, max: 90 }, longitudeRange: { min: -180, max: 180 } },
    latitude: { latitudePrecision: 4, latitudeRange: { min: -90, max: 90 } },
    longitude: { longitudePrecision: 4, longitudeRange: { min: -180, max: 180 } },
    attachment: { maxSize: 10, maxWidth: 1000, maxHeight: 1000 },
    image: { maxSize: 10, maxWidth: 1000, maxHeight: 1000 },
    document: { maxSize: 10 },
    video: { maxSize: 10 },
};

const isMissing = (value: any) => value === undefined || value === null;

/**
 * A picklist needs a value source: a Domain Data reference (dynamic) or at least one static entry.
 * Without either, `reconcileFieldForSubmit` infers `static` and the backend rejects the save with
 * "staticPicklist is required" - technically correct but baffling on a field where the user never
 * chose "static". Catch it in the UI instead and name the actual missing decision.
 *
 * Returns antd-shaped `errorFields` so it can be fed straight to `handleValidationFailure`, which
 * already selects the offending row, scrolls to it, and renders a row-aware banner.
 */
const getUnconfiguredPicklistErrors = (fields: any[] | undefined): { name: (string | number)[]; errors: string[] }[] => {
    const errors: { name: (string | number)[]; errors: string[] }[] = [];

    (fields || []).forEach((field, index) => {
        if (!field || field.dataType !== "picklist") {
            return;
        }

        const hasDomain = !isMissing(field.dataDomainId) && field.dataDomainId !== "";
        const hasStaticValues = Array.isArray(field.staticPicklist) && field.staticPicklist.some((item: any) => item && item.code);

        if (hasDomain || hasStaticValues) {
            return;
        }

        // Point at the control the user actually needs to touch for the picklist type in effect.
        const prop = field.picklistType === "static" ? "staticPicklist" : "dataDomainId";
        errors.push({
            name: ["fields", index, prop],
            errors: ["Select a Domain Data source, or switch Picklist Type to Static and add values"],
        });
    });

    return errors;
};

/**
 * Reconciles one field for submission. The submit payload is built from `updatedSchemaModel` (a shadow
 * React state fed only by `onValuesChange`), so anything the form knows but that handler never saw is
 * missing. Precedence, weakest to strongest:
 *   1. per-dataType defaults  2. live antd form values  3. the shadow state (deliberately curated,
 *      e.g. `staticPicklist` is filtered down to entries with a code - it must win).
 */
const reconcileFieldForSubmit = (field: any, formField: any, dataType: string | undefined): any => {
    const defaults = (dataType && DATA_TYPE_REQUIRED_DEFAULTS[dataType]) || {};
    const merged: any = { ...defaults, ...(formField || {}) };

    // The shadow state wins, but never let an explicit undefined/null clobber a resolved value.
    for (const [key, value] of Object.entries(field || {})) {
        if (!isMissing(value) || isMissing(merged[key])) {
            merged[key] = value;
        }
    }

    // Backfill anything still unresolved (panel never mounted, so no initialValue registered).
    for (const [key, value] of Object.entries(defaults)) {
        if (isMissing(merged[key])) {
            merged[key] = value;
        }
    }

    // picklistType has no single safe default - it depends on how the user configured the field.
    if (merged.dataType === "picklist" && isMissing(merged.picklistType)) {
        merged.picklistType = merged.dataDomainId ? "dynamic" : "static";
    }

    return merged;
};

const ManageSchemaModelForm = React.forwardRef<IManageSchemaModelFormRef, IManageSchemaModelFormProps>(
    (
        {
            schemaModel,
            nameRules,
            descriptionRules,
            readOnly = true,
            allowSave = false,
            header,
            footer,
            userCommentsPermission,
            openFieldCommentId,
            openFieldAnnotationId,
            businessAreaDataList,
            onFinish,
            getFormErrors,
            onSwitchEditMode,
            onCancel,
            onExportSchema,
            getFieldRowLevelError,
            getBusinessAreaDataForBusinessAreaResult,
            getDataTagsByBusinessAreaIdResult,
            dataTagsByBusinessAreaIdResult,
            autoTagButton,
            autoTaggingResult,
            autoTaggingData,
            formErrors,
            isAutoTagging,
            isFetchingTags,
            isAutoTagRequested,
            autoTagError,
        },
        ref,
    ) => {
        const [form] = useForm<ISchemaModel>();
        const [selectedFieldRow, setSelectedFieldRow] = React.useState<FormListFieldData | null>(null);
        const [selectedFieldDataType, setSelectedFieldDataType] = React.useState<string | null>(null);
        const [updatedSchemaModel, setUpdatedSchemaModal] = React.useState<ISchemaModel>(schemaModel);
        const [showSaveConfirmationModal, setShowSaveConfirmationModal] = React.useState<boolean>(false);
        const [showSaveDraftConfirmationModal, setShowSaveDraftConfirmationModal] = React.useState<boolean>(false);

        const prevSchemaIdRef = React.useRef<string | undefined>(undefined);
        const prevVersionRef = React.useRef<number | undefined>(undefined);

        React.useEffect(() => {
            if (schemaModel && form) {
                const isFirstLoad = prevSchemaIdRef.current === undefined;
                const schemaChanged = prevSchemaIdRef.current !== schemaModel.schemaId;
                const incomingVersion = (schemaModel as any).version;
                const versionAdvanced =
                    !isFirstLoad &&
                    !schemaChanged &&
                    typeof incomingVersion === "number" &&
                    typeof prevVersionRef.current === "number" &&
                    incomingVersion > prevVersionRef.current;

                if (!isFirstLoad && !schemaModel.schemaId) return;
                if (!isFirstLoad && !schemaChanged && !versionAdvanced) return;

                prevSchemaIdRef.current = schemaModel.schemaId;
                prevVersionRef.current = incomingVersion;

                const editedSchemaModel = _.cloneDeep(schemaModel);

                form.resetFields();
                if (editedSchemaModel.fields && Array.isArray(editedSchemaModel.fields)) {
                    editedSchemaModel.fields
                        .filter((field) => field.dataType === "datetime" || field.dataType === "date" || field.dataType === "time")
                        .forEach((field) => {
                            if (field.max !== null && field.max !== undefined && field.min !== null && field.min !== undefined) {
                                let format = null;
                                // Convert min/max to range for date and time
                                switch (field.dataType) {
                                    case "datetime":
                                        format = `${_.replace(field.dateFormat ?? "", /\$/g, field.dateSeparator ?? "")} ${field.timeFormat}`;
                                        field.range = [dayjs(dayjs(field.min, "YYYY-MM-DDTHH:mm:ss"), format), dayjs(dayjs(field.max, "YYYY-MM-DDTHH:mm:ss"), format)];

                                        delete field.min;
                                        delete field.max;
                                        break;
                                    case "date":
                                        format = `${_.replace(field.dateFormat ?? "", /\$/g, field.dateSeparator ?? "")}`;
                                        field.range = [dayjs(dayjs(field.min, "YYYY-MM-DDTHH:mm:ss"), format), dayjs(dayjs(field.max, "YYYY-MM-DDTHH:mm:ss"), format)];

                                        delete field.min;
                                        delete field.max;
                                        break;
                                    case "time":
                                        format = field.timeFormat;
                                        field.range = [dayjs.utc(field.min), dayjs.utc(field.max)];

                                        delete field.min;
                                        delete field.max;
                                        break;
                                    default:
                                        break;
                                }
                            }
                        });
                }
                form.setFieldsValue(editedSchemaModel);
                setUpdatedSchemaModal(editedSchemaModel);
            }
        }, [form, schemaModel]); 

        const performAutoTagging = React.useCallback(
            (classifierTags: IDataTag[] = []) => {
                const currentName = form.getFieldValue("name");
                const currentDescription = form.getFieldValue("description");

                if (!currentName || !updatedSchemaModel.fields || updatedSchemaModel.fields.length === 0) {
                    message.error("Please provide schema name and at least one field before auto-tagging.");
                    autoTagButton?.onAutoTagCancel?.();
                    return;
                }

                const payload = {
                    schemaName: currentName,
                    schemaDescription: currentDescription,
                    DataClassifiers: classifierTags.map((tag) => ({ tagName: tag.tagName })),
                    fields: updatedSchemaModel.fields.map((field, index) => ({
                        fieldId: field.fieldId || `temp_${index}`,
                        fieldName: field.name,
                        FieldDescription: field.description || "",
                        dataType: field.dataType,
                        classifiers: field.classifiers || [],
                    })),
                };

                autoTagButton?.onAutoTagging(payload);
            },
            [autoTagButton, form, updatedSchemaModel],
        );

        const showNoTagsWarning = React.useCallback(() => {
            Modal.confirm({
                title: "No Classifier Tags Available",
                content: (
                    <div>
                        <p>No classifier tags are currently available in the system.</p>
                        <p>Auto-tagging will provide limited suggestions without existing tag context.</p>
                        <p className="mt-2 text-sm text-gray-600">For better results, please ensure classifier tags are configured in your business area first.</p>
                    </div>
                ),
                okText: "Proceed Anyway",
                cancelText: "Cancel",
                centered: true,
                onCancel: () => {
                    autoTagButton?.onAutoTagCancel?.();
                },
                onOk: () => performAutoTagging([]),
                icon: <InfoCircleTwoTone twoToneColor="#FF9800" />,
            });
        }, [performAutoTagging, autoTagButton]);


        const onFieldRowSelected = React.useCallback(
            (fieldRow: FormListFieldData) => {
                if (selectedFieldRow && _.isEqual(selectedFieldRow, fieldRow)) {
                    return;
                }

                if (fieldRow && form) {
                    const _dataTypeValue = form.getFieldValue(["fields", fieldRow.name, "dataType"]);
                    setSelectedFieldRow(fieldRow);
                    setSelectedFieldDataType(_dataTypeValue);
                }
            },
            [form, selectedFieldRow],
        );

        const onFieldRowDelete = React.useCallback(
            (removeAction: () => void, deleteContext?: FieldDeleteContext) => {
                const totalRecordCount = Number((schemaModel as any)?.totalRecordCount ?? (form as any).getFieldValue("totalRecordCount") ?? 0);
                const isPrimaryKeyField = !!deleteContext?.fieldData?.isPrimaryKey;

                if (totalRecordCount > 0 && isPrimaryKeyField) {
                    message.error(`Cannot remove primary key field when schema has ${totalRecordCount} existing records. Remove all records first.`);
                    return;
                }

                setSelectedFieldRow(null);
                setSelectedFieldDataType(null);
                removeAction();
            },
            [form, schemaModel],
        );

        const onFieldDataTypeChange = React.useCallback(
            (dataTypeValue: string, field: FormListFieldData) => {
                if (!form) {
                    return;
                }

                const previousDataType = selectedFieldDataType;
                let min = 0;
                let max = 2000;

                if (dataTypeValue in fieldDataTypes) {
                    const dataType = fieldDataTypes[dataTypeValue as keyof typeof fieldDataTypes];
                    if (_.has(dataType, "min")) {
                        min = typeof dataType.min === "number" ? dataType.min : 0;
                    }
                    if (_.has(dataType, "max")) {
                        max = typeof dataType.max === "number" ? dataType.max : 2000;
                    }
                }

                const currentField = form.getFieldValue(["fields", field.name]);
                delete currentField.min;
                delete currentField.max;
                delete currentField.range;
                delete currentField.dateFormat;
                delete currentField.dateSeparator;
                delete currentField.timeFormat;
                delete currentField.precision;
                delete currentField.maxHeight;
                delete currentField.maxWidth;
                delete currentField.maxSize;

                if (previousDataType === "picklist" && dataTypeValue !== "picklist") {
                    delete currentField.picklistType;
                }

                if (dataTypeValue === "date") {
                    currentField.dateFormat = "MM$DD$YYYY";
                    currentField.dateSeparator = "/";
                    currentField.range = [dayjs("01/01/1970", "MM/DD/YYYY"), dayjs("01/01/2099", "MM/DD/YYYY")];
                } else if (dataTypeValue === "datetime") {
                    currentField.dateFormat = "MM$DD$YYYY";
                    currentField.dateSeparator = "/";
                    currentField.timeFormat = "hh:mm:ss a";
                    const initialDateTimeFormat = "MM/DD/YYYY hh:mm:ss a";
                    currentField.range = [dayjs("01/01/1970 12:00:00 am", initialDateTimeFormat), dayjs("01/01/2099 11:59:59 pm", initialDateTimeFormat)];
                } else if (dataTypeValue === "time") {
                    currentField.timeFormat = "hh:mm:ss a";
                    currentField.range = [dayjs.utc(dayjs()), dayjs.utc(dayjs().utc().add(12, "hours"))];
                } else if (dataTypeValue === "image" || dataTypeValue === "attachment") {
                    // Set up the default values
                    currentField.maxHeight = 1000;
                    currentField.maxWidth = 1000;
                    currentField.maxSize = 10;
                } else if (dataTypeValue === "document") {
                    currentField.maxSize = 10;
                } else {
                    // For others, set to default min/max
                    currentField.max = max;
                    currentField.min = min;
                }

                form.setFieldValue(["fields", field.name], currentField);

                setSelectedFieldDataType(dataTypeValue);
                setUpdatedSchemaModal((prev) => {
                    const editedFields = _.cloneDeep(prev.fields);
                    editedFields[field.name] = {
                        ...(editedFields[field.name] ?? {}),
                        ...currentField,
                    };
                    return { ...prev, fields: editedFields };
                });
            },
            [form, selectedFieldDataType],
        );

        const selectedField = Form.useWatch(["fields", selectedFieldRow?.name], form);
        useEffect(() => {
            if (selectedFieldRow && selectedField && selectedField.dataType === "picklist" && selectedField.picklistType === "static") {
                const picklistItems = selectedField.staticPicklist?.filter((item: IDomainDataResultItem) => !!item.code) || [];
                setUpdatedSchemaModal((prev) => {
                    const editedSchemaModel = _.cloneDeep(prev);
                    const currentField = editedSchemaModel.fields[selectedFieldRow.name];
                    currentField.staticPicklist = picklistItems;
                    return editedSchemaModel;
                });
            }
        }, [selectedField, selectedFieldRow]);

        const handleValuesChanged = React.useCallback((changedValues: Partial<ISchemaModel>, allValues: ISchemaModel) => {
            const changedFields = changedValues.fields;
            if (!changedFields) return;
            
            setUpdatedSchemaModal((prev) => {
                const editedFields = [...(prev.fields || [])];
                for (const [key, value] of Object.entries(changedFields)) {
                    const index = Number(key);
                    editedFields[index] = {
                        ..._.cloneDeep(editedFields[index] ?? allValues.fields[index] ?? {}),
                        ...(value ?? {}),
                    };
                }
                return { ...prev, fields: editedFields };
            });
        }, []);

        const handleRemoveField = React.useCallback((index: number) => {
            setUpdatedSchemaModal((prev) => {
                const editedFields = [...(prev.fields || [])];
                editedFields.splice(index, 1);
                return { ...prev, fields: editedFields };
            });
        }, []);

        const formListAddRef = React.useRef<(defaultValue?: any) => void>(() => {});
        const formListRemoveRef = React.useRef<(index: number) => void>(() => {});
        const formRef = React.useMemo(() => ({ current: form }), [form]);

        const stableAdd = React.useCallback(() => {
            formListAddRef.current({
                dataType: "string",
                isPrimaryKey: false,
                isSortKey: false,
                active: true,
                isRequired: false,
                isMultivalue: false,
                isPII: false,
                max: 500,
                min: 1,
                survivorshipRule: "UPDATE_IF_SOURCE_NOT_NULL",
            });
        }, []);

        const stableRemove = React.useCallback(
            (index: number) => {
                formListRemoveRef.current(index);
                handleRemoveField(index);
            },
            [handleRemoveField],
        );

        const handleExportSchema = React.useCallback(() => {
            if (!!schemaModel.schemaId) {
                const { name, description } = form.getFieldsValue(["name", "description"]);
                const currentPayload = { ...updatedSchemaModel, name, description };
                if (_.isEqual(schemaModel, currentPayload) === false) {
                    setShowSaveConfirmationModal(true);
                } else {
                    onExportSchema(schemaModel.schemaId);
                }
            }
        }, [form, onExportSchema, schemaModel, updatedSchemaModel]);

        const buildSubmitPayload = React.useCallback((): ISchemaModel => {
            const { name, description } = form.getFieldsValue(["name", "description"]);
            const payload = { ...updatedSchemaModel, name, description };

            if (payload.fields) {
                const formFields = form.getFieldValue("fields") || [];
                const hasPrimaryKey = payload.fields.some((f) => f.isPrimaryKey);
                payload.fields = payload.fields.map((field, index) => {
                    const formField = formFields[index];
                    const dataType = field?.dataType ?? formField?.dataType;
                    const nextField = reconcileFieldForSubmit(field, formField, dataType);

                    if (!hasPrimaryKey || nextField.isPrimaryKey) {
                        const { survivorshipRule, ...rest } = nextField;
                        return rest;
                    }
                    return nextField;
                });
            }

            return payload;
        }, [form, updatedSchemaModel]);

        const handleValidationFailure = React.useCallback(
            (errorInfo: any) => {
                const errorFields: { name: (string | number)[]; errors: string[] }[] = errorInfo?.errorFields ?? [];

                if (!errorFields.length) {
                    // No structured info from antd — fall back to the generic message.
                    message.error("Validation failed. Please check the form fields.");
                    return;
                }

                const firstName = Array.isArray(errorFields[0]?.name) ? errorFields[0].name : [errorFields[0]?.name];
                if (firstName[0] === "fields" && typeof firstName[1] === "number") {
                    const rowIndex = firstName[1] as number;
                    onFieldRowSelected({ name: rowIndex, key: rowIndex } as FormListFieldData);
                }

                try {
                    form.scrollToField(firstName as any, { behavior: "smooth", block: "center" });
                } catch {
                    // scrollToField can throw if the field isn't mounted yet; ignore.
                }

                const MAX_SHOWN = 4;
                const summaries = errorFields.slice(0, MAX_SHOWN).map(({ name, errors }) => {
                    const nameArr = Array.isArray(name) ? name : [name];
                    let label: string;
                    if (nameArr[0] === "fields" && typeof nameArr[1] === "number") {
                        const idx = nameArr[1] as number;
                        const fieldName = form.getFieldValue(["fields", idx, "name"]) || `Field ${idx + 1}`;
                        const prop = nameArr.length > 2 ? humanizeFieldProp(String(nameArr[nameArr.length - 1])) : "";
                        label = prop ? `${fieldName} \u2192 ${prop}` : `${fieldName}`;
                    } else {
                        label = humanizeFieldProp(String(nameArr[nameArr.length - 1]));
                    }
                    const detail = Array.isArray(errors) ? errors.join(", ") : errors;
                    return `${label}: ${detail}`;
                });

                const remaining = errorFields.length - summaries.length;
                const suffix = remaining > 0 ? ` (+${remaining} more)` : "";
                const count = errorFields.length;
                message.error({
                    content: `Fix ${count} field ${count === 1 ? "error" : "errors"} before saving \u2014 ${summaries.join(" | ")}${suffix}`,
                    duration: 8,
                });
            },
            [form, onFieldRowSelected],
        );

        /**
         * Gate a built payload on picklist configuration. Returns the payload when it is safe to send,
         * or null after surfacing a row-aware error. antd's own `required` rule on Domain Data cannot
         * cover this: it only validates MOUNTED Form.Items, so a field whose properties panel was never
         * opened slips through `validateFields()` untouched.
         */
        const gateSubmitPayload = React.useCallback(
            (payload: ISchemaModel): ISchemaModel | null => {
                const picklistErrors = getUnconfiguredPicklistErrors(payload.fields as any[]);
                if (picklistErrors.length) {
                    handleValidationFailure({ errorFields: picklistErrors });
                    return null;
                }
                return payload;
            },
            [handleValidationFailure],
        );

        const onSaveDraftConfirmClick = React.useCallback(() => {
            setShowSaveDraftConfirmationModal(false);
            form.validateFields()
                .then(() => {
                    const payload = gateSubmitPayload(buildSubmitPayload());
                    if (payload) {
                        onFinish(payload);
                    }
                })
                .catch(handleValidationFailure);
        }, [onFinish, buildSubmitPayload, form, handleValidationFailure, gateSubmitPayload]);

        const onSaveUnchangedConfirmClick = React.useCallback(() => {
            setShowSaveConfirmationModal(false);
            form.validateFields()
                .then(() => {
                    const payload = gateSubmitPayload(buildSubmitPayload());
                    if (!payload) {
                        return;
                    }
                    if (payload.schemaId) {
                        onFinish(payload, () => onExportSchema(payload.schemaId));
                    } else {
                        onFinish(payload);
                    }
                })
                .catch(handleValidationFailure);
        }, [form, onExportSchema, onFinish, buildSubmitPayload, handleValidationFailure, gateSubmitPayload]);

        const findTagIdByName = useCallback(
            (tagName: string) => {
                const allClassifierTags = dataTagsByBusinessAreaIdResult?.Items || [];
                const tag = allClassifierTags.find((t: any) => t.tagName === tagName);
                return tag?.tagId || null;
            },
            [dataTagsByBusinessAreaIdResult],
        );

        React.useImperativeHandle(
            ref,
            () => ({
                requestAutoTagging: () => {
                    // State is managed by the parent, so nothing to do here directly.
                },
                performAutoTagging: (classifierTags: any[] = []) => performAutoTagging(classifierTags),
                showNoTagsWarning: () => showNoTagsWarning(),
                onApplyAutoTagSuggestions: (selectedSuggestions: any) => {
                    const autoTagSuggestions = autoTaggingData?.data?.suggestions || [];
                    const currentFields: ISchemaField[] = _.cloneDeep(updatedSchemaModel.fields || []);
                    let fieldsUpdated = false;

                    const fieldIdToIndexMap: Record<string, number> = {};
                    currentFields.forEach((field, index) => {
                        if (field.fieldId) {
                            fieldIdToIndexMap[field.fieldId] = index;
                        } else {
                            fieldIdToIndexMap[`temp_${index}`] = index;
                        }
                    });
                    autoTagSuggestions.forEach((suggestion: any) => {
                        const fieldIndex = fieldIdToIndexMap[suggestion.fieldId];
                        if (fieldIndex !== undefined && selectedSuggestions[suggestion.fieldId]) {
                            const fieldSelection = selectedSuggestions[suggestion.fieldId];
                            if (fieldSelection.additionalTags) {
                                const tagsToAdd = suggestion.additionalTags.filter((tag: any) => fieldSelection.additionalTags[tag.tagName]);
                                if (tagsToAdd.length > 0) {
                                    const existingTags = currentFields[fieldIndex].classifiers || [];
                                    const existingTagNames = new Set(existingTags.map((t: any) => t.tagName));

                                    const newTags = tagsToAdd
                                        .filter((tag: any) => !existingTagNames.has(tag.tagName))
                                        .map((tag: any) => ({
                                            tagName: tag.tagName,
                                            tagId: findTagIdByName(tag.tagName),
                                        }));

                                    if (newTags.length > 0) {
                                        currentFields[fieldIndex].classifiers = [...existingTags, ...newTags];
                                        fieldsUpdated = true;
                                    }
                                }
                            }

                            if (fieldSelection.mismatchedTags) {
                                const tagsToRemove = suggestion.mismatchedTags.filter((tag: any) => fieldSelection.mismatchedTags[tag.tagName]);

                                if (tagsToRemove.length > 0) {
                                    const tagsToRemoveSet = new Set(tagsToRemove.map((t: any) => t.tagName));
                                    currentFields[fieldIndex].classifiers = (currentFields[fieldIndex].classifiers || []).filter((tag: any) => !tagsToRemoveSet.has(tag.tagName));
                                    fieldsUpdated = true;
                                }
                            }
                        }
                    });

                    if (fieldsUpdated) {
                        form.setFieldsValue({ fields: currentFields });
                        setUpdatedSchemaModal((prev) => ({
                            ...prev,
                            fields: currentFields,
                        }));
                        form.validateFields(); // Optional: Trigger standard re-validation natively
                        message.success("Auto-tagging suggestions applied successfully.");
                    } else {
                        message.info("No changes were made to the schema fields.");
                    }
                },
                applyAiPiiSuggestions: (updatedFields: any[]) => {
                    const currentFields: ISchemaField[] = _.cloneDeep(updatedSchemaModel.fields || []);
                    let fieldsUpdated = false;

                    // Create a map of fieldId to updated field data
                    const updatedFieldsMap = new Map(updatedFields.map((field) => [field.fieldId, field]));

                    currentFields.forEach((field, index) => {
                        const updatedField = updatedFieldsMap.get(field.fieldId);
                        if (updatedField) {
                            // Update isPII flag
                            if (field.isPII !== updatedField.isPII) {
                                currentFields[index].isPII = updatedField.isPII;
                                fieldsUpdated = true;
                            }

                            // Update classifiers
                            if (updatedField.classifiers && JSON.stringify(field.classifiers) !== JSON.stringify(updatedField.classifiers)) {
                                currentFields[index].classifiers = updatedField.classifiers;
                                fieldsUpdated = true;
                            }
                        }
                    });

                    if (fieldsUpdated) {
                        form.setFieldsValue({ fields: currentFields });
                        setUpdatedSchemaModal((prev) => ({
                            ...prev,
                            fields: currentFields,
                        }));
                        message.success("PII suggestions applied to form. Click 'Save as Draft' to save changes.");
                    } else {
                        message.info("No PII changes were applied to the schema fields.");
                    }
                },
                applyAiFieldDescriptions: (updatedFields: any[], schemaDescription?: string | null) => {
                    const currentFields: ISchemaField[] = _.cloneDeep(updatedSchemaModel.fields || []);
                    let fieldsUpdated = false;
                    let schemaDescUpdated = false;

                    // Update schema description if provided
                    if (schemaDescription) {
                        const currentSchemaDesc = form.getFieldValue("description");
                        if (currentSchemaDesc !== schemaDescription) {
                            form.setFieldsValue({ description: schemaDescription });
                            schemaDescUpdated = true;
                        }
                    }

                    // Create a map of fieldId to updated field data
                    const updatedFieldsMap = new Map(updatedFields.map((field) => [field.fieldId, field]));

                    currentFields.forEach((field, index) => {
                        const updatedField = updatedFieldsMap.get(field.fieldId);
                        if (updatedField && field.description !== updatedField.description) {
                            currentFields[index].description = updatedField.description;
                            fieldsUpdated = true;
                        }
                    });

                    if (fieldsUpdated) {
                        form.setFieldsValue({ fields: currentFields });
                        setUpdatedSchemaModal((prev) => ({
                            ...prev,
                            fields: currentFields,
                        }));
                    }

                    if (fieldsUpdated || schemaDescUpdated) {
                        const updates = [];
                        if (fieldsUpdated) updates.push("field descriptions");
                        if (schemaDescUpdated) updates.push("schema description");
                        message.success(`AI ${updates.join(" and ")} applied to form. Click 'Save as Draft' to save changes.`);
                    } else {
                        message.info("No description changes were applied.");
                    }
                },
            }),
            [autoTaggingData, findTagIdByName, form, updatedSchemaModel, performAutoTagging, showNoTagsWarning],
        );

        return (
            <>
                <Modal
                    title={
                        <div className="flex items-center">
                            <span className="font-semibold">Schema Update</span>
                        </div>
                    }
                    centered={true}
                    open={showSaveDraftConfirmationModal}
                    onOk={onSaveDraftConfirmClick}
                    onCancel={() => setShowSaveDraftConfirmationModal(false)}
                    okText="Proceed"
                    okButtonProps={{
                        "data-testid": CypressTestIds.MANAGE_SCHEMA_MODEL_PROMPT_PROCEED_BUTTON,
                    }}
                    cancelText="Cancel"
                    width={450}
                >
                    <div className="mb-2 flex items-start">
                        <div className="pr-4">
                            <InfoCircleTwoTone twoToneColor="#FF9800" style={{ fontSize: "22px" }} />
                        </div>
                        <div>
                            <p className="mb-1 text-base font-normal">Schema changes will trigger a background indexing process.</p>
                            <p className="text-sm font-light text-gray-600">Validation results may be temporarily inconsistent until indexing completes.</p>
                        </div>
                    </div>
                </Modal>
                <Modal title="Save Schema" centered={true} open={showSaveConfirmationModal} onOk={onSaveUnchangedConfirmClick} onCancel={() => setShowSaveConfirmationModal(false)}>
                    <p>There are unsaved changes. Do you want to save schema?</p>
                </Modal>
                <Form form={form} preserve name="basic" onValuesChange={handleValuesChanged} className="relative flex size-full flex-1 flex-col">
                    <Splitter className="flex size-full flex-1 gap-4 overflow-auto">
                        <Splitter.Panel min="40%" max="80%">
                            <FlexContainerLayout
                                headerClassName="gap-4 justify-between [&_.ant-form-item-label]:!pb-1 [&_.ant-form-item-label]:text-xs [&_.ant-form-item-label]:font-bold"
                                header={
                                    <>
                                        <div className="flex-1">
                                            <Form.Item
                                                layout="vertical"
                                                className="mb-0"
                                                {...getFormErrors("name")}
                                                label="Schema Name"
                                                name={["name"]}
                                                validateFirst={true}
                                                rules={nameRules}
                                            >
                                                <Input readOnly={readOnly} variant="filled" data-testid={CypressTestIds.MANAGE_SCHEMA_MODEL_NAME_INPUT} />
                                            </Form.Item>
                                        </div>
                                        <div className="flex-1">
                                            <Form.Item layout="vertical" className="mb-0" label="Schema Description" name={["description"]} rules={descriptionRules}>
                                                <Input readOnly={readOnly} variant="filled" data-testid={CypressTestIds.MANAGE_SCHEMA_MODEL_DESCRIPTION_INPUT} />
                                            </Form.Item>
                                        </div>
                                        {header}
                                    </>
                                }
                                footer={
                                    readOnly ? (
                                        <div className="flex w-full flex-1 justify-end gap-4">
                                            <Button data-testid={CypressTestIds.MANAGE_SCHEMA_MODEL_CANCEL_BUTTON} onClick={onCancel} icon={<CaretLeftOutlined />}>
                                                Back to List
                                            </Button>
                                            {!!onSwitchEditMode && (
                                                <Button type="primary" onClick={() => onSwitchEditMode(schemaModel.schemaId)} icon={<EditOutlined />}>
                                                    Edit Mode
                                                </Button>
                                            )}
                                            {!!schemaModel.schemaId && (
                                                <Button data-testid={CypressTestIds.MANAGE_SCHEMA_MODEL_EXPORT_BUTTON} type="primary" onClick={handleExportSchema}>
                                                    Export
                                                </Button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex w-full flex-1 justify-between gap-4">
                                            <div className="flex gap-4">{footer}</div>
                                            <div className="flex gap-4">
                                                <Button data-testid={CypressTestIds.MANAGE_SCHEMA_MODEL_CANCEL_BUTTON} onClick={onCancel}>
                                                    Cancel
                                                </Button>
                                                <Button
                                                    data-testid={CypressTestIds.MANAGE_SCHEMA_MODEL_SAVE_AS_DRAFT_BUTTON}
                                                    type="primary"
                                                    onClick={() => setShowSaveDraftConfirmationModal(true)}
                                                    disabled={readOnly || !allowSave}
                                                >
                                                    Save as Draft
                                                </Button>
                                                <Tooltip title="Feature coming soon">
                                                    <Button data-testid={CypressTestIds.MANAGE_SCHEMA_MODEL_SAVE_PUBLISH_BUTTON} type="primary" disabled={true}>
                                                        Save & Publish
                                                    </Button>
                                                </Tooltip>
                                                {!!schemaModel.schemaId && (
                                                    <SchemaPromotionAction
                                                        businessAreaId={(schemaModel as any).businessAreaId}
                                                        schemaId={schemaModel.schemaId}
                                                        schemaName={schemaModel.name}
                                                        promotionStatus={(schemaModel as any).promotionStatus}
                                                        promotedVersion={(schemaModel as any).promotedVersion}
                                                    />
                                                )}
                                                {!!schemaModel.schemaId && (
                                                    <Button data-testid={CypressTestIds.MANAGE_SCHEMA_MODEL_EXPORT_BUTTON} type="primary" onClick={handleExportSchema}>
                                                        Export
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )
                                }
                            >
                                <Form.List name="fields">
                                    {(fields, { add, remove }) => {
                                        formListAddRef.current = add;
                                        formListRemoveRef.current = remove;
                                        return readOnly ? (
                                            <SchemaModelFieldListReadOnly
                                                formRef={formRef}
                                                fields={fields}
                                                onFieldRowSelected={onFieldRowSelected}
                                                getFieldRowLevelError={getFieldRowLevelError}
                                            />
                                        ) : (
                                            <SchemaModelFieldList
                                                formRef={formRef}
                                                fields={fields}
                                                add={stableAdd}
                                                remove={stableRemove}
                                                readOnly={readOnly}
                                                onFieldRowSelected={onFieldRowSelected}
                                                onFieldRowDelete={onFieldRowDelete}
                                                onDataTypeChange={onFieldDataTypeChange}
                                                formErrors={formErrors}
                                                getFormErrors={getFormErrors}
                                                getFieldRowLevelError={getFieldRowLevelError}
                                                userCommentsPermission={userCommentsPermission}
                                                openFieldCommentId={openFieldCommentId}
                                                openFieldAnnotationId={openFieldAnnotationId}
                                            />
                                        );
                                    }}
                                </Form.List>
                            </FlexContainerLayout>
                        </Splitter.Panel>
                        <Splitter.Panel defaultSize={300} min={100} max={400}>
                            <SchemaModelFieldProperties
                                selectedFieldRow={selectedFieldRow}
                                fieldDataType={selectedFieldDataType || ""}
                                formRef={{ current: form }}
                                readOnly={readOnly}
                                getFormErrors={getFormErrors}
                                getBusinessAreaDataForBusinessAreaResult={getBusinessAreaDataForBusinessAreaResult}
                                businessAreaDataList={businessAreaDataList}
                            ></SchemaModelFieldProperties>
                        </Splitter.Panel>
                    </Splitter>
                </Form>
            </>
        );
    },
);
export default ManageSchemaModelForm;
